import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Upload, ClipboardPaste, Loader2 } from "lucide-react";
import api, { formatApiError } from "../api";
import { Button, Modal, Textarea } from "./common";

/**
 * Transcript ingestion entry point. Shared by the Meetings list and the
 * DealDetail "Meetings" card so both behave identically.
 *
 * Deliberately not drag-only: the drop target is a <label> wrapping a real file
 * input, so it works with a tap on mobile (where there is no drag & drop at all)
 * and with a keyboard. The paste path is a button opening a textarea rather than
 * a global ctrl+V listener -- hijacking paste app-wide would break every other
 * input on the page.
 *
 * `meetingId` attaches the transcript to an existing meeting; without it the
 * backend creates a draft meeting around the content (the content-first flow).
 */
export default function TranscriptDropzone({
  meetingId, dealId, projectId, contactId, onUploaded, compact = false,
}) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [over, setOver] = useState(false);
  const [pasteOpen, setPasteOpen] = useState(false);
  const [text, setText] = useState("");
  const inputRef = useRef(null);

  const done = (meeting) => {
    setError("");
    if (onUploaded) onUploaded(meeting);
  };

  const fail = (e) => setError(formatApiError(e.response?.data?.detail) || e.message);

  async function sendFile(file) {
    if (!file) return;
    setBusy(true);
    setError("");
    try {
      const form = new FormData();
      form.append("file", file);
      if (meetingId) form.append("meeting_id", meetingId);
      if (dealId) form.append("deal_id", dealId);
      if (projectId) form.append("project_id", projectId);
      if (contactId) form.append("contact_id", contactId);
      form.append("subject", file.name.replace(/\.[^.]+$/, ""));
      const r = await api.post("/meetings/upload", form);
      done(r.data);
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  async function sendText() {
    const body = text.trim();
    if (!body) return;
    setBusy(true);
    setError("");
    try {
      let id = meetingId;
      if (!id) {
        // No meeting yet: create the draft first, then attach the pasted text.
        // Same two rows the file path produces, just without a filename.
        const created = await api.post("/meetings", {
          subject: t("meet.pastedTitle"),
          deal_id: dealId || null,
          project_id: projectId || null,
          contact_id: contactId || null,
        });
        id = created.data.id;
      }
      const r = await api.post(`/meetings/${id}/transcript`, { text: body, source: "manual_paste" });
      setText("");
      setPasteOpen(false);
      done(r.data);
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-col sm:flex-row gap-2">
        <label
          data-testid="transcript-dropzone"
          onDragOver={(e) => { e.preventDefault(); setOver(true); }}
          onDragLeave={() => setOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setOver(false);
            sendFile(e.dataTransfer.files?.[0]);
          }}
          className={`flex-1 flex items-center justify-center gap-3 cursor-pointer rounded-sm border border-dashed transition-colors duration-200 ${
            compact ? "px-4 py-4" : "px-6 py-8"
          } ${over ? "border-primary bg-primary/5" : "border-border hover:border-primary/60 hover:bg-surface"}`}
        >
          <input
            ref={inputRef}
            type="file"
            accept=".txt,.md,.vtt,.docx"
            className="sr-only"
            disabled={busy}
            onChange={(e) => sendFile(e.target.files?.[0])}
          />
          {busy ? (
            <Loader2 size={18} className="animate-spin text-primary" />
          ) : (
            <Upload size={18} className="text-muted shrink-0" />
          )}
          <span className="text-sm text-center">
            <span className="font-medium">{busy ? t("meet.uploading") : t("meet.dropHere")}</span>
            {!compact && <span className="block text-xs text-muted mt-0.5">{t("meet.dropHint")}</span>}
          </span>
        </label>
        <Button variant="ghost" disabled={busy} onClick={() => setPasteOpen(true)} className="sm:w-auto">
          <ClipboardPaste size={16} /> {t("meet.pasteInstead")}
        </Button>
      </div>

      {error && <p data-testid="dropzone-error" className="text-sm text-danger">{error}</p>}

      <Modal
        open={pasteOpen}
        onClose={() => setPasteOpen(false)}
        title={t("meet.pasteTitle")}
        footer={
          <>
            <Button variant="ghost" onClick={() => setPasteOpen(false)}>{t("common.cancel")}</Button>
            <Button onClick={sendText} disabled={busy || !text.trim()}>{t("meet.startExtraction")}</Button>
          </>
        }
      >
        <p className="text-sm text-muted">{t("meet.pasteHint")}</p>
        <Textarea
          rows={14}
          value={text}
          data-testid="paste-textarea"
          onChange={(e) => setText(e.target.value)}
          placeholder={t("meet.pastePlaceholder")}
        />
      </Modal>
    </div>
  );
}
